import React, { useState, useEffect } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import SideNav from "./pages/sideNav";
import TopNavSuperAdmin from "./pages/topNav";
import AuthServ from "../service/AuthServ";
import "../css/adminLayout.css";
import { usePreference } from "../context/PreferenceContext";
import RoleToggle from "../component/RoleToggle";

// Import Tabler Icons
import {
  IconLayoutDashboard,
  IconPackage,
  IconBuildingStore,
  IconAdjustmentsHorizontal,
  IconMessageCircle,
  IconAlertTriangle,
  IconUsers,
  IconUser,
  IconBell,
  IconTicket,
  IconLanguage,
  IconCurrencyDollar,
  IconTruckDelivery,
  IconSettings,
  IconBoxSeam,
  IconHierarchy2,
  IconGitBranch,
  IconCertificate,
  IconListDetails,
  IconTag,
  IconMessage2,
  IconChartBar,
  IconGift,
  IconBan,
  IconArrowBack,
  IconSparkles,
} from '@tabler/icons-react';

const AdminLayout = () => {
  const { t } = usePreference();
  const [isSideNavOpen, setIsSideNavOpen] = useState(false);
  const location = useLocation();

  const navigate = useNavigate();

  const handleDashboardClick = () => {
    navigate("/admin/dashboard"); // ✅ smooth route change, no full reload
  };

  const role = localStorage.getItem("role");
  const permissionsString = localStorage.getItem("permissions");

  // ✅ Ultra-Robust Permission Normalization
  // "Manage_Products" -> "manageproducts"
  // "Full Access" -> "fullaccess"
  const normalize = (str) => {
    if (!str || typeof str !== 'string') return '';
    return str.toLowerCase().replace(/[\s\-_]/g, "");
  };

  const rawPermissions = permissionsString ? JSON.parse(permissionsString) : [];
  const permissions = rawPermissions.map(p => normalize(p));


  // ✅ Close sidenav on small screens
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 768) setIsSideNavOpen(false);
      else setIsSideNavOpen(true);
    };
    window.addEventListener("resize", handleResize);
    handleResize(); // run once initially
    return () => window.removeEventListener("resize", handleResize);
  }, []);



  // ✅ Close sidenav when route changes (on mobile or tablet)
  useEffect(() => {
    if (window.innerWidth <= 768) setIsSideNavOpen(false);
  }, [location.pathname]);

  // ✅ Permission Sync: Always fetch latest permissions for Admin to handle revocation/updates
  useEffect(() => {
    const syncPermissions = async () => {
      // Only sync for admins (Super Admin has implicit full access)
      if (role === 'admin') {
        try {
          const res = await AuthServ.single(true); // Calls /auth/me with force refresh
          if (res?.data?.permissions) {
            const newPermissions = JSON.stringify(res.data.permissions);
            const currentPermissions = localStorage.getItem("permissions");

            // If permissions changed (e.g. revoked by Super Admin), update and reload
            if (newPermissions !== currentPermissions) {
              console.log("Admin Layout: Permissions changed, syncing...");
              localStorage.setItem("permissions", newPermissions);
              window.location.reload();
            }
          }
        } catch (err) {
          console.error("Admin Layout: Failed to sync permissions", err);
        }
      }
    };

    syncPermissions();
  }, [role]);

  const toggleSideNav = () => setIsSideNavOpen((prev) => !prev);




  // ✅ Debugging: Display permissions on screen if menus are missing
  // const debugInfo = role !== 'superadmin' ? (
  //   <div style={{ position: 'fixed', bottom: 10, left: 10, background: 'black', color: 'lime', padding: 10, fontSize: 12, zIndex: 9999, maxWidth: 300, wordWrap: 'break-word', border: '2px solid red' }}>
  //     <strong>DEBUG MODE:</strong><br />
  //     <strong>Role:</strong> {role}<br />
  //     <strong>Raw Permissions:</strong> {Array.isArray(rawPermissions) ? rawPermissions.join(", ") : JSON.stringify(rawPermissions)}<br />
  //     <strong>Normalized:</strong> {permissions.join(", ")}
  //   </div>
  // ) : null;

  // Debugging logs to help identify why menus are hidden
  console.log("Admin Layout Debug:", { role, permissions });

  const hasAccess = (requiredPermission) => {
    // 1. Super Admin always has access
    if (role === 'superadmin') return true;

    // 2. Check Global Full Access Override
    if (permissions.includes("fullaccess")) return true;

    // 3. Fallback: Check if original raw permissions included it
    if (rawPermissions.includes("full_access") || rawPermissions.includes("FULL_ACCESS")) return true;

    // 4. If no specific permission required, allow access
    if (!requiredPermission) return true;

    // 🌟 5. Special Case: Sellers can access User Management (restricted view)
    if (role === 'seller' && requiredPermission === 'manage_users') return true;

    // 6. Check specific permission
    const req = normalize(requiredPermission);
    return permissions.includes(req);
  };

  const adminItems = [
    {
      icon: <IconLayoutDashboard size={20} />,
      label: t('dashboard'),
      key: "admin-dashboard",
      path: "/admin/dashboard",
      onClick: handleDashboardClick,
    },
    {
      icon: <IconBuildingStore size={20} />,
      label: t('shop_management'),
      key: "manage-admin-shops",
      permission: "manage_sellers",
      children: [
        {
          icon: <IconUsers size={18} />,
          label: t('seller_investors'),
          key: "seller-investors",
          path: "/admin/seller-investors",
          permission: "manage_sellers",
        },
        {
          icon: <IconBuildingStore size={18} />,
          label: t('shop'),
          key: "shop",
          path: "/admin/shop",
          permission: "manage_sellers",
        },
        {
          icon: <IconMessage2 size={18} />,
          label: "Shop Ratings",
          key: "shop-ratings",
          path: "/admin/shop-ratings",
          permission: "manage_sellers",
        },

      ].filter(item => hasAccess(item.permission)),

    },
    {
      icon: <IconPackage size={20} />,
      label: t('product_management'),
      key: "manage-admin-products",
      permission: "manage_products",
      children: [
        {
          icon: <IconBoxSeam size={18} />,
          label: t('products'),
          key: "products-catalog",
          path: "/admin/products-catalog",
          permission: "manage_products",
        },
        {
          icon: <IconHierarchy2 size={18} />,
          label: t('categories'),
          key: "categories",
          path: "/admin/categories",
          permission: "manage_categories",
        },
        {
          icon: <IconGitBranch size={18} />,
          label: t('sub_categories'),
          key: "sub-categories",
          path: "/admin/sub-categories",
          permission: "manage_categories",
        },
        {
          icon: <IconCertificate size={18} />,
          label: t('brands'),
          key: "brands",
          path: "/admin/brands",
          permission: "manage_brands",
        },
        {
          icon: <IconListDetails size={18} />,
          label: t('product_options'),
          key: "product-options",
          path: "/admin/product-options",
          permission: "manage_products",
        },
        {
          icon: <IconTag size={18} />,
          label: t('product_tags'),
          key: "products-tags",
          path: "/admin/product-tags",
          permission: "manage_products",
        },
        {
          icon: <IconMessage2 size={18} />,
          label: t('product_feedback'),
          key: "product-feedback",
          path: "/admin/product-feedback",
          permission: "manage_products",
        },
        {
          icon: <IconChartBar size={18} />,
          label: t('inventory_thresholds'),
          key: "product-inventory-thresholds",
          path: "/admin/product-inventory-thresholds",
          permission: "manage_products",
        },
        {
          icon: <IconAdjustmentsHorizontal size={18} />,
          label: "Markup Rate Settings",
          key: "product-settings",
          path: "/admin/product-settings",
          permission: "manage_settings",
        },
        {
          icon: <IconSparkles size={18} />,
          label: t('lifestyle_needs', 'Lifestyle & Needs'),
          key: "lifestyles",
          path: "/admin/lifestyles",
          permission: "manage_products",
        },
      ].filter(item => hasAccess(item.permission)),
    },

    {
      icon: <IconListDetails size={20} />,
      label: "Order Management",
      key: "manage-admin-orders",
      permission: "manage_orders",
      children: [
        {
          icon: <IconListDetails size={18} />,
          label: "Orders",
          key: "admin-orders",
          path: "/admin/orders",
          permission: "manage_orders",
        },
        {
          icon: <IconBan size={18} />,
          label: "Cancel Orders",
          key: "admin-cancellations",
          path: "/admin/cancellations",
          permission: "manage_orders",
        },
        {
          icon: <IconArrowBack size={18} />,
          label: "Return Orders",
          key: "admin-returns",
          path: "/admin/returns",
          permission: "manage_orders",
        },
        {
          icon: <IconAlertTriangle size={18} />,
          label: "Return Rules",
          key: "admin-return-rules",
          path: "/admin/return-rules",
          permission: "manage_orders",
        },
        {
          icon: <IconCurrencyDollar size={18} />,
          label: "Seller Payout History",
          key: "seller-payouts",
          path: "/admin/seller-payouts",
          permission: "manage_orders",
        },
      ].filter(item => hasAccess(item.permission)),
    },
    {
      icon: <IconUsers size={20} />,
      label: t('user_management'),
      key: "manage-admin-user",
      permission: "manage_users",
      children: [
        {
          icon: <IconUser size={18} />,
          label: t('users'),
          key: "user",
          path: "/admin/user",
          permission: "manage_users",
        },
        {
          icon: <IconAdjustmentsHorizontal size={18} />,
          label: t('seller_requests'),
          key: "seller-requests",
          path: "/admin/seller-requests",
          permission: "manage_sellers",
        },
        {
          icon: <IconAdjustmentsHorizontal size={18} />,
          label: t('admin_permissions'),
          key: "admin-permissions",
          path: "/admin/admin-permissions",
        },
        {
          icon: <IconMessageCircle size={18} />,
          label: t('messages'),
          key: "admin-messages",
          path: "/admin/messages",
          permission: "manage_messages",
        },
      ].filter(item => {
        if (item.key === 'admin-permissions') {
          return role === 'superadmin' || hasAccess('full_access');
        }
        return hasAccess(item.permission);
      }),
    },
    // 🚚 Delivery Management
    {
      icon: <IconTruckDelivery size={20} />,
      label: t('delivery_management'),
      key: "delivery-management",
      permission: "manage_deliveries",
      children: [
        {
          icon: <IconTruckDelivery size={18} />,
          label: "Delivery Operations",
          key: "admin-delivery-ops",
          path: "/admin/delivery-ops",
          permission: "manage_deliveries",
        },
        {
          icon: <IconUser size={18} />,
          label: t('delivery_staff'),
          key: "admin-delivery-staff",
          path: "/admin/delivery-staff",
          permission: "manage_deliveries",
        },
        {
          icon: <IconCurrencyDollar size={18} />,
          label: "Delivery Payout History",
          key: "delivery-payouts",
          path: "/admin/delivery-payouts",
          permission: "manage_deliveries",
        },
        {
          icon: <IconAdjustmentsHorizontal size={18} />,
          label: t('delivery_settings'),
          key: "admin-delivery-settings",
          path: "/admin/delivery-settings",
          permission: "manage_settings",
        },
      ].filter(item => hasAccess(item.permission)),
    },
    {
      icon: <IconTicket size={20} />,
      label: t('marketing'),
      key: "marketing",
      children: [
        {
          icon: <IconTicket size={18} />,
          label: t('coupons'),
          key: "admin-coupons",
          path: "/admin/coupons",
          permission: "manage_coupons",
        },
        {
          icon: <IconAdjustmentsHorizontal size={18} />,
          label: "Banners",
          key: "admin-banners",
          path: "/admin/banners",
          permission: "manage_banners",
        },
        {
          icon: <IconBell size={18} />,
          label: "Offers",
          key: "admin-offers",
          path: "/admin/offers",
          permission: "manage_offers",
        },
        {
          icon: <IconGift size={18} />,
          label: "Referral Settings",
          key: "referral-settings",
          path: "/admin/referral-settings",
          permission: "manage_settings",
        },
      ].filter(item => hasAccess(item.permission)),
    },
    {
      icon: <IconUser size={20} />,
      label: t('account'),
      key: "profile-menu",
      children: [
        {
          icon: <IconUser size={18} />,
          label: t('personal_info'),
          key: "profile",
          path: "/admin/profile?tab=personal",
        },
        {
          icon: <IconAdjustmentsHorizontal size={18} />,
          label: t('my_addresses'),
          key: "admin-addresses",
          path: "/admin/profile?tab=address",
        },
        {
          icon: <IconAdjustmentsHorizontal size={18} />,
          label: t('profile_settings'),
          key: "admin-settings",
          path: "/admin/profile?tab=settings",
        },
      ],
    },
    {
      icon: <IconSettings size={20} />,
      label: t('settings'),
      key: "settings-group",
      permission: "manage_settings",
      children: [
        {
          icon: <IconAdjustmentsHorizontal size={18} />,
          label: t('import_data'),
          key: "import-data",
          path: "/admin/import",
          permission: "manage_settings",
        },
        {
          icon: <IconAdjustmentsHorizontal size={18} />,
          label: t('export_data'),
          key: "export-data",
          path: "/admin/export",
          permission: "manage_settings",
        },
        {
          icon: <IconLanguage size={18} />,
          label: t('manage_language'),
          key: "manage-language",
          path: "/admin/language",
          permission: "manage_settings",
        },
        {
          icon: <IconCurrencyDollar size={18} />,
          label: t('manage_currency'),
          key: "manage-currency",
          path: "/admin/currency",
          permission: "manage_settings",
        },
      ].filter(item => hasAccess(item.permission)),
    },
  ].filter(item => {
    if (item.key === 'profile-menu') return true;
    if (item.children) return item.children.length > 0;
    return hasAccess(item.permission);
  });

  // Handle menu click navigation


  return (
    <div className="layout">
      {/* {debugInfo} */}
      <TopNavSuperAdmin onToggle={toggleSideNav} />
      <div className={`main-content ${isSideNavOpen ? 'sidenav-open' : ''}`}>
        <div className="sidenav-overlay" onClick={() => setIsSideNavOpen(false)}></div>
        <div className="sidenav-container">
          <RoleToggle />
          <SideNav
            isOpen={isSideNavOpen}
            setIsOpen={setIsSideNavOpen}
            items={adminItems} />
        </div>
        <div className="page-content">
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default AdminLayout;
